// src/pages/Skoyteslip.jsx
// /skoyteslip — forenklet ferdigmelding av skøyteslip for butikkansatte, i startsidens stil
// (planer/ny-kundesamhandling-plan.md). «Ny skøyteslip» går til «velg kunde» og åpner
// skøyteslip-bestillingen i butikkmodus (handlingen skoyteslip-ny i handlinger.js).
// Skøyteslip er vanlige verkstedservicer (WorkshopService, kategori "skoyteslip"):
// de registreres i Mottak og redigeres i WorkshopDetails. Her vises de åpne i
// butikken med «Ferdig – send SMS» (status avsluttet + SMS med lenke til
// betalingsinformasjonen) og servicefakturaen for betaling i kassen.
// Backend: src/routes/skoyteslip.js.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import SmsTilKunden from "../components/SmsTilKunden";
import { useDialog, useSideTittel } from "../components/tilgjengelighet";
import ServiceInvoiceModal from "../features/invoice/ServiceInvoiceModal";
import { tilDelelinjer, beregnTotaler } from "../features/invoice/fakturaGrunnlag";
import { fmtNOK, fmtDT, fmtDate, fmtMin, toShortRefFront } from "../utils/format";
import { fillWorkshopSmsTemplate } from "../utils/smsUtils";
import { meldSmsSendt } from "../utils/smsStatus";
import { FARGER, FORSIDE, STI } from "../ny-kundesamhandling/handlinger";
import Ikon from "../ny-kundesamhandling/Ikon";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// Samme som Servicelisten (WorkshopList): butikk fra cookien.
function valgtButikk() {
  const id = Cookies.get("butikkid") || "";
  if (id === "1") return "Røyken";
  if (id === "2") return "Slemmestad";
  const navn = (Cookies.get("selectedStore") || "").toLowerCase();
  if (navn.includes("slemmestad")) return "Slemmestad";
  if (navn.includes("røyken") || navn.includes("royken")) return "Røyken";
  return "";
}

const erSkoyteMal = (t) =>
  String(t?.type || "").toLowerCase().replace(/ø/g, "o") === "skoyteslip" && t?.status !== "inaktiv";

const kundeNavn = (svc) =>
  `${svc?.customerSnapshot?.firstName || ""} ${svc?.customerSnapshot?.lastName || ""}`.trim();

const avsluttetTid = (svc) => {
  const logg = (svc?.produkt?.statusLogs || []).filter((l) => l.status === "avsluttet");
  return logg.length ? logg[logg.length - 1].at : svc?.updatedAt;
};

const totalFor = (svc) =>
  beregnTotaler(svc.serviceLines || [], tilDelelinjer(svc.partsLines), svc.fraktLines || []).total;

async function lesFeil(r, standard) {
  try {
    const j = await r.json();
    return j?.message || standard;
  } catch {
    return standard;
  }
}

export default function Skoyteslip() {
  useSideTittel("Skøyteslip");
  const butikk = valgtButikk();

  const [liste, setListe] = useState([]);
  const [lastet, setLastet] = useState(false);
  const [feil, setFeil] = useState("");
  const [maler, setMaler] = useState([]);
  const [ferdigmeld, setFerdigmeld] = useState(null); // servicen dialogen gjelder
  const [faktura, setFaktura] = useState(null); // servicen fakturaen vises for

  const hent = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE_URL}/skoyteslip?butikk=${encodeURIComponent(butikk)}`);
      if (!r.ok) throw new Error(await lesFeil(r, "Kunne ikke hente skøyteslip"));
      setListe(await r.json());
      setFeil("");
    } catch (e) {
      setFeil(e.message || "Kunne ikke hente skøyteslip");
    } finally {
      setLastet(true);
    }
  }, [butikk]);

  useEffect(() => {
    hent();
  }, [hent]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/smstemplates`);
        const data = r.ok ? await r.json() : [];
        setMaler((Array.isArray(data) ? data : data.items || []).filter(erSkoyteMal));
      } catch {
        setMaler([]);
      }
    })();
  }, []);

  const oppdater = (svc) => {
    setListe((l) => l.map((s) => (s._id === svc._id ? svc : s)));
    setFaktura((f) => (f && f._id === svc._id ? svc : f));
  };

  const aapne = liste.filter((s) => !["avsluttet", "kansellert"].includes(s.status));
  const ferdige = liste.filter((s) => s.status === "avsluttet");

  const knappSekundar =
    "inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-xl border-2 border-slate-300 bg-white px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50";

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-10">
      <Link to={FORSIDE} className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 font-medium text-indigo-700 hover:bg-indigo-50">
        <Ikon navn="tilbake" className="w-5 h-5" /> Tilbake til kontrollpanelet
      </Link>

      <header className="flex flex-wrap items-center gap-3">
        <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${FARGER.verksted.ikon}`}>
          <Ikon navn="skoyte" className="w-6 h-6" />
        </span>
        <div className="min-w-0 flex-1 basis-56">
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 text-balance">Skøyteslip{butikk ? ` – ${butikk}` : ""}</h1>
          <p className="text-slate-600">Send ferdigmelding når skøytene er slipt.</p>
        </div>
        <Link
          to={`${STI}/skoyteslip-ny`}
          className="inline-flex min-h-[2.75rem] items-center gap-2 rounded-2xl border-2 border-emerald-300 bg-emerald-50 px-3 py-1.5 font-bold text-slate-900 transition hover:bg-emerald-100 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
        >
          <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${FARGER.verksted.ikon}`}>
            <Ikon navn="pluss" className="w-6 h-6" />
          </span>
          Ny skøyteslip
        </Link>
      </header>

      {feil && (
        <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">
          {feil}
        </p>
      )}

      <section aria-labelledby="sk-apne" className="space-y-2">
        <h2 id="sk-apne" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
          Venter på sliping
          {lastet && <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-sm font-semibold text-emerald-900">{aapne.length}</span>}
        </h2>
        {!lastet ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter skøyteslip …</p>
        ) : aapne.length === 0 ? (
          <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">
            Ingen skøyter venter på sliping{butikk ? ` i ${butikk}` : ""}. Trykk «Ny skøyteslip» når en kunde leverer.
          </p>
        ) : (
          <ul className="grid gap-2 md:grid-cols-2">
            {aapne.map((s) => (
              <li key={s._id} className="flex min-w-0 flex-col gap-2 rounded-2xl border-2 border-slate-200 bg-white p-3">
                <div className="min-w-0">
                  <p className="text-base font-bold leading-tight text-slate-900 [overflow-wrap:anywhere]">{kundeNavn(s) || "Ukjent kunde"}</p>
                  <p className="font-mono text-sm text-slate-600">{s.customerSnapshot?.phoneNumber || "Mangler telefon"}</p>
                </div>
                <div className="min-w-0 text-slate-800">
                  <p className="[overflow-wrap:anywhere]">
                    {(s.serviceLines || []).map((l) => l.navn).join(", ") || "Ingen arbeid lagt inn"}
                  </p>
                  <p className="text-sm text-slate-600">
                    Ref. {toShortRefFront(s.serviceRef) || s.serviceRef || "—"} · {fmtNOK(totalFor(s))} · levert {fmtDT(s.createdAt)}
                  </p>
                </div>
                <div className="mt-auto flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setFerdigmeld(s)}
                    className="inline-flex min-h-[2.5rem] flex-1 basis-48 items-center justify-center gap-2 rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 active:scale-[0.98] touch-manipulation focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
                  >
                    <Ikon navn="hake" className="w-5 h-5" /> Ferdig – send SMS
                  </button>
                  <Link to={`/workshop/${s._id}`} className={knappSekundar}>
                    Åpne
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {ferdige.length > 0 && (
        <section aria-labelledby="sk-ferdige" className="space-y-2">
          <h2 id="sk-ferdige" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
            Ferdige siste døgn
            <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-sm font-semibold text-slate-700">{ferdige.length}</span>
          </h2>
          <ul className="divide-y divide-slate-200 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {ferdige.map((s) => (
              <li key={s._id} className="flex flex-wrap items-center gap-2 px-3 py-1.5">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
                  <Ikon navn="hake" className="w-5 h-5" />
                </span>
                <div className="min-w-0 flex-1 basis-40">
                  <p className="font-semibold text-slate-900 [overflow-wrap:anywhere]">{kundeNavn(s) || "Ukjent kunde"}</p>
                  <p className="text-sm text-slate-600">
                    Ref. {toShortRefFront(s.serviceRef) || s.serviceRef || "—"} · ferdigmeldt {fmtDT(avsluttetTid(s))}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" onClick={() => setFaktura(s)} className={knappSekundar}>
                    <Ikon navn="kvittering" className="w-5 h-5" /> Servicefaktura
                  </button>
                  <Link to={`/workshop/${s._id}`} className={knappSekundar}>
                    Åpne
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {ferdigmeld && (
        <FerdigmeldDialog
          svc={ferdigmeld}
          maler={maler}
          butikk={butikk}
          onLukk={() => setFerdigmeld(null)}
          onFerdig={oppdater}
          onFaktura={(svc) => {
            setFerdigmeld(null);
            setFaktura(svc);
          }}
        />
      )}

      {faktura && <Faktura svc={faktura} onLukk={() => setFaktura(null)} onSvcUpdate={oppdater} />}
    </div>
  );
}

// Servicefakturaen som i WorkshopDetails (samme komponent og beløp).
function Faktura({ svc, onLukk, onSvcUpdate }) {
  const serviceLines = useMemo(() => svc.serviceLines || [], [svc]);
  const partLines = useMemo(() => tilDelelinjer(svc.partsLines), [svc]);
  const totals = useMemo(
    () => beregnTotaler(serviceLines, partLines, svc.fraktLines || []),
    [serviceLines, partLines, svc]
  );
  const p = svc.produkt || {};
  const produkt = {
    merke: p.produktmerke || "Produkt",
    type: p.produkttype || "",
    farge: p.farge || "",
    butikk: p.butikk || "",
    leveringsDato: p.leveringsDato || null,
    forventetFerdigDato: p.forventetFerdigDato || null,
  };

  return (
    <ServiceInvoiceModal
      open
      onClose={onLukk}
      svc={svc}
      customer={svc.customerSnapshot || {}}
      produkt={produkt}
      serviceLines={serviceLines}
      partLines={partLines}
      totals={totals}
      workDesc={svc.arbeidsbeskrivelse || ""}
      checklistSummary={[]}
      checklistComplete={false}
      fmtNOK={fmtNOK}
      fmtDT={fmtDT}
      fmtDate={fmtDate}
      fmtMin={fmtMin}
      onSvcUpdate={onSvcUpdate}
    />
  );
}

function FerdigmeldDialog({ svc, maler, butikk, onLukk, onFerdig, onFaktura }) {
  // {lenke} beholdes i teksten; serveren setter inn kundelenken (eller legger den bakerst).
  const fyll = useCallback(
    (mal) =>
      fillWorkshopSmsTemplate(mal, {
        customer: svc.customerSnapshot,
        svc,
        produkt: { butikk: svc.produkt?.butikk },
        customerLink: "{lenke}",
      }),
    [svc]
  );

  const [malId, setMalId] = useState(maler[0]?._id || "");
  const [tekst, setTekst] = useState(maler[0] ? fyll(maler[0]) : "");
  const [medLenke, setMedLenke] = useState(true);
  const [sender, setSender] = useState(false);
  const [feil, setFeil] = useState("");
  const [ferdig, setFerdig] = useState(null); // oppdatert service etter sending
  const dialogRef = useDialog(true, sender ? () => {} : onLukk);

  // Malene kan komme etter at dialogen er åpnet.
  useEffect(() => {
    if (!malId && maler[0] && !tekst) {
      setMalId(maler[0]._id);
      setTekst(fyll(maler[0]));
    }
  }, [maler, malId, tekst, fyll]);

  const velgMal = (id) => {
    setMalId(id);
    const mal = maler.find((m) => m._id === id);
    if (mal) setTekst(fyll(mal));
  };

  const send = async (e) => {
    e.preventDefault();
    if (sender) return;
    if (!tekst.trim()) {
      setFeil("Skriv en melding før du sender.");
      return;
    }
    setSender(true);
    setFeil("");
    try {
      const r = await fetch(`${API_BASE_URL}/skoyteslip/${svc._id}/ferdigmeld`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          tekst: medLenke ? tekst : tekst.replace(/\{(lenke|link)\}/g, "").trim(),
          medLenke,
          ansatt: Cookies.get("selectedEmployee") || "",
          butikk,
        }),
      });
      if (!r.ok) throw new Error(await lesFeil(r, "Kunne ikke sende SMS."));
      const oppdatert = await r.json();
      meldSmsSendt();
      setFerdig(oppdatert);
      onFerdig(oppdatert);
    } catch (err) {
      setFeil(err.message || "Kunne ikke sende SMS.");
    } finally {
      setSender(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-3 overflow-y-auto">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ferdigmeld-tittel"
        className="w-full max-w-lg rounded-2xl bg-white p-3 shadow-xl sm:p-5"
      >
        <h2 id="ferdigmeld-tittel" className="mb-1 text-lg font-bold text-slate-900">
          Ferdigmeld skøyteslip
        </h2>
        <p className="mb-4 text-slate-700 [overflow-wrap:anywhere]">
          {kundeNavn(svc) || "Ukjent kunde"} · {svc.customerSnapshot?.phoneNumber || "mangler telefon"} · ref.{" "}
          {toShortRefFront(svc.serviceRef) || svc.serviceRef || "—"}
        </p>

        {ferdig ? (
          <>
            <p role="status" className="mb-3 flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 font-medium text-emerald-900">
              <Ikon navn="hake" className="w-5 h-5 shrink-0" />
              Servicen er avsluttet og SMS-en er sendt.
            </p>
            <SmsTilKunden telefon={svc.customerSnapshot?.phoneNumber} />
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={onLukk}
                className="min-h-[2.5rem] rounded-xl border-2 border-slate-300 px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                Lukk
              </button>
              <button
                type="button"
                data-autofokus
                onClick={() => onFaktura(ferdig)}
                className="min-h-[2.5rem] rounded-xl bg-indigo-700 px-3 py-1.5 font-semibold text-white hover:bg-indigo-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                Skriv ut servicefaktura
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={send} className="space-y-2">
            {maler.length > 1 && (
              <div>
                <label htmlFor="ferdigmeld-mal" className="block font-semibold text-slate-800">
                  SMS-mal
                </label>
                <select
                  id="ferdigmeld-mal"
                  value={malId}
                  onChange={(e) => velgMal(e.target.value)}
                  className="mt-1 block w-full rounded-xl border-2 border-slate-300 bg-white p-2.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
                >
                  {maler.map((m) => (
                    <option key={m._id} value={m._id}>
                      {m.tittel}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {maler.length === 0 && (
              <p className="text-slate-700">
                Fant ingen SMS-mal for skøyteslip. Skriv meldingen selv, eller lag en mal under SMS-maler.
              </p>
            )}
            <div>
              <label htmlFor="ferdigmeld-tekst" className="block font-semibold text-slate-800">
                Melding til kunden
              </label>
              <textarea
                id="ferdigmeld-tekst"
                value={tekst}
                onChange={(e) => setTekst(e.target.value)}
                rows={5}
                maxLength={1000}
                className="mt-1 block w-full rounded-xl border-2 border-slate-300 p-2.5 focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/30"
              />
            </div>
            <label className="flex min-h-[2.25rem] items-center gap-2 text-slate-800">
              <input type="checkbox" checked={medLenke} onChange={(e) => setMedLenke(e.target.checked)} className="h-5 w-5" />
              Ta med lenke til betalingsinformasjon
            </label>
            {feil && (
              <p role="alert" className="rounded-xl border border-red-200 bg-red-50 p-2.5 text-red-800">
                {feil}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onLukk}
                disabled={sender}
                className="min-h-[2.5rem] rounded-xl border-2 border-slate-300 px-3 py-1.5 font-semibold text-slate-800 hover:bg-slate-50 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                Avbryt
              </button>
              <button
                type="submit"
                disabled={sender}
                className="min-h-[2.5rem] rounded-xl bg-emerald-700 px-3 py-1.5 font-bold text-white hover:bg-emerald-800 disabled:opacity-60 focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50"
              >
                {sender ? "Sender …" : "Avslutt og send SMS"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
